/**
 * Animated Button — GSAP Animations
 *
 * Handles all client-side animation for .animated-button elements.
 * Requires GSAP core (+ optional plugins loaded separately).
 *
 * Animations supported:
 *  Hover  : scale | slide | magnetic | icon-diagonal
 *  Click  : ripple
 *  Entry  : fade-up | fade-in | slide-left | slide-right
 *  Icon   : spin-on-hover
 *  Text   : animates label independently when data-animate-text="true"
 */

(function () {
  "use strict";

  // ── Guard: GSAP must be present ───────────────────────────────────────────
  if (typeof gsap === "undefined") {
    console.warn("[AnimatedButton] GSAP not found. Animations skipped.");
    return;
  }

  // ── Reduced-motion check ──────────────────────────────────────────────────
  const prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;

  // ── Intersection Observer for entrance animations ─────────────────────────
  const observerOptions = {
    threshold: 0.15,
    rootMargin: "0px 0px -40px 0px",
  };

  // ── Utility: clamp ────────────────────────────────────────────────────────
  function clamp(val, min, max) {
    return Math.max(min, Math.min(max, val));
  }

  // ─────────────────────────────────────────────────────────────────────────
  //  HOVER: scale / bounce
  // ─────────────────────────────────────────────────────────────────────────
  function initScale(btn, icon, label, animateIcon, animateText) {
    const targets = [];
    if (animateIcon && icon) targets.push(icon);
    if (animateText && label) targets.push(label);
    const mainTarget = targets.length ? targets : btn;

    btn.addEventListener("mouseenter", () => {
      gsap.to(mainTarget, {
        scale: 1.08,
        duration: 0.25,
        ease: "back.out(2)",
      });
    });

    btn.addEventListener("mouseleave", () => {
      gsap.to(mainTarget, {
        scale: 1,
        duration: 0.2,
        ease: "power2.out",
      });
    });
  }

  // ─────────────────────────────────────────────────────────────────────────
  //  HOVER: slide (background shimmer sweep)
  // ─────────────────────────────────────────────────────────────────────────
  function initSlide(btn) {
    // Inject shimmer pseudo-overlay
    const shimmer = document.createElement("span");
    shimmer.classList.add("animated-button__shimmer");
    shimmer.setAttribute("aria-hidden", "true");
    btn.appendChild(shimmer);
    btn.style.overflow = "hidden";

    gsap.set(shimmer, { x: "-110%", skewX: -15 });

    btn.addEventListener("mouseenter", () => {
      gsap.to(shimmer, {
        x: "110%",
        duration: 0.5,
        ease: "power2.out",
      });
    });

    btn.addEventListener("mouseleave", () => {
      gsap.set(shimmer, { x: "-110%" });
    });
  }

  // ─────────────────────────────────────────────────────────────────────────
  //  HOVER: magnetic
  // ─────────────────────────────────────────────────────────────────────────
  function initMagnetic(btn) {
    const strength = 0.35;

    btn.addEventListener("mousemove", (e) => {
      const rect = btn.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = (e.clientX - cx) * strength;
      const dy = (e.clientY - cy) * strength;

      gsap.to(btn, {
        x: clamp(dx, -20, 20),
        y: clamp(dy, -10, 10),
        duration: 0.4,
        ease: "power2.out",
      });
    });

    btn.addEventListener("mouseleave", () => {
      gsap.to(btn, {
        x: 0,
        y: 0,
        duration: 0.5,
        ease: "elastic.out(1, 0.4)",
      });
    });
  }

  // ─────────────────────────────────────────────────────────────────────────
  //  HOVER: icon-diagonal (icon moves ↗ then resets)
  // ─────────────────────────────────────────────────────────────────────────
  function initIconDiagonal(btn, icon, label, animateIcon, animateText) {
    const iconTargets = animateIcon && icon ? icon : null;
    const labelTargets = animateText && label ? label : null;

    btn.addEventListener("mouseenter", () => {
      if (iconTargets) {
        gsap.to(iconTargets, {
          x: 5,
          y: -5,
          duration: 0.25,
          ease: "power2.out",
        });
      }
      if (labelTargets) {
        gsap.to(labelTargets, {
          x: 3,
          duration: 0.25,
          ease: "power2.out",
        });
      }
    });

    btn.addEventListener("mouseleave", () => {
      const all = [iconTargets, labelTargets].filter(Boolean);
      if (all.length) {
        gsap.to(all, {
          x: 0,
          y: 0,
          duration: 0.2,
          ease: "power2.in",
        });
      }
    });
  }

  // ─────────────────────────────────────────────────────────────────────────
  //  CLICK: ripple
  // ─────────────────────────────────────────────────────────────────────────
  function initRipple(btn) {
    const container = btn.querySelector(".animated-button__ripple-container");
    if (!container) return;

    btn.addEventListener("click", (e) => {
      const rect = btn.getBoundingClientRect();
      const size = Math.max(rect.width, rect.height) * 2;
      const x = e.clientX - rect.left - size / 2;
      const y = e.clientY - rect.top - size / 2;

      const ripple = document.createElement("span");
      ripple.classList.add("animated-button__ripple");
      ripple.style.cssText = `
                width: ${size}px;
                height: ${size}px;
                left: ${x}px;
                top: ${y}px;
            `;
      container.appendChild(ripple);

      gsap.fromTo(
        ripple,
        { scale: 0, opacity: 0.45 },
        {
          scale: 1,
          opacity: 0,
          duration: 0.55,
          ease: "power2.out",
          onComplete: () => ripple.remove(),
        },
      );
    });
  }

  // ─────────────────────────────────────────────────────────────────────────
  //  ICON: spin on hover
  // ─────────────────────────────────────────────────────────────────────────
  function initIconSpin(btn, icons) {
    if (!icons.length) return;

    btn.addEventListener("mouseenter", () => {
      gsap.to(icons, {
        rotation: 360,
        duration: 0.5,
        ease: "power1.inOut",
      });
    });

    btn.addEventListener("mouseleave", () => {
      gsap.set(icons, { rotation: 0 });
    });
  }

  // ─────────────────────────────────────────────────────────────────────────
  //  ENTRANCE animations (IntersectionObserver triggered)
  // ─────────────────────────────────────────────────────────────────────────
  const entranceMap = {
    "fade-up": { opacity: 0, y: 30 },
    "fade-in": { opacity: 0 },
    "slide-left": { opacity: 0, x: -40 },
    "slide-right": { opacity: 0, x: 40 },
  };

  function initEntrance(wrapper, type) {
    const from = entranceMap[type];
    if (!from) return;

    gsap.set(wrapper, from);

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          gsap.to(wrapper, {
            opacity: 1,
            x: 0,
            y: 0,
            duration: 0.65,
            ease: "power3.out",
            delay: 0.05,
          });
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    observer.observe(wrapper);
  }

  // ─────────────────────────────────────────────────────────────────────────
  //  Bootstrap all buttons on the page
  // ─────────────────────────────────────────────────────────────────────────
  function init() {
    const buttons = document.querySelectorAll(".animated-button");
    if (!buttons.length) return;

    buttons.forEach((btn) => {
      const wrapper = btn.closest(".animated-button-wrapper") || btn;

      const hoverAnim = btn.dataset.hoverAnimation || "none";
      const clickAnim = btn.dataset.clickAnimation || "none";
      const entranceAnim = btn.dataset.entranceAnimation || "none";
      const spinIcon = btn.dataset.iconSpin === "true";
      const animateIcon = btn.dataset.animateIcon === "true";
      const animateText = btn.dataset.animateText === "true";

      const iconBefore = btn.querySelector(".animated-button__icon--before");
      const iconAfter = btn.querySelector(".animated-button__icon--after");
      const label = btn.querySelector(".animated-button__label");
      const allIcons = [iconBefore, iconAfter].filter(Boolean);

      // Primary icon target: prefer "after" for diagonal / spin
      const primaryIcon = iconAfter || iconBefore || null;

      // ── Entrance (always, even with reduced motion we only skip hover/click)
      if (entranceAnim !== "none") {
        initEntrance(wrapper, entranceAnim);
      }

      if (prefersReducedMotion) return;

      // ── Hover animations
      switch (hoverAnim) {
        case "scale":
          initScale(btn, primaryIcon, label, animateIcon, animateText);
          break;
        case "slide":
          initSlide(btn);
          break;
        case "magnetic":
          initMagnetic(btn);
          break;
        case "icon-diagonal":
          initIconDiagonal(btn, primaryIcon, label, animateIcon, animateText);
          break;
        default:
          break;
      }

      // ── Click animations
      if (clickAnim === "ripple") {
        initRipple(btn);
      }

      // ── Icon spin
      if (spinIcon && allIcons.length) {
        initIconSpin(btn, allIcons);
      }
    });
  }

  // ── Run after DOM ready ───────────────────────────────────────────────────
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  // ── Re-run for any dynamically injected buttons (e.g. Ajax page loads) ───
  if (typeof MutationObserver !== "undefined") {
    const mo = new MutationObserver(() => init());
    mo.observe(document.body, { childList: true, subtree: true });
  }
})();
